'use client'
// useSigninForm.ts
import { DEFAULT_LANDING_ID, DEFAULT_SUBACCOUNT_ID } from '@/utils/constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useOTPForm } from './useOTPForm'
import {
  signInWithEmail
} from '@/lib/auth-service'

import { createClient } from '@/utils/supabase/client'

export const signinFormSchema = z.object({
  email: z.string().email({ message: 'Enter a valid email' }),
  password: z
    .string()
    .min(4, 'Password length should be at least 4 characters')
    .max(24, 'Password cannot exceed more than 24 characters')
})

export type SigninFormValues = z.infer<typeof signinFormSchema>

export const useSigninForm = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    getValues,
    watch
  } = useForm({
    resolver: zodResolver(signinFormSchema)
  })

  const onSubmit = async (inputData: FieldValues, goToOtp: () => void) => {
    setIsLoading(true)

    try {
      const response = await signInWithEmail({
        email: inputData.email,
        password: inputData.password
      })
      if (response) {
        localStorage.setItem('user', JSON.stringify(response))
        localStorage.removeItem('logout_event')
        localStorage.removeItem('is_logging_out')

        // Set Supabase session for proper authentication
        if (response.userInfo?.session) {
          const { createClient } = await import('@/utils/supabase/client')
          const supabase = createClient()
          const session = response.userInfo.session

          // Handle both object and dict session formats
          const accessToken = session.access_token || session.accessToken
          const refreshToken = session.refresh_token || session.refreshToken

          if (accessToken && refreshToken) {
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
          }
        }

        const plansParam = searchParams.get('plans')
        if (plansParam === 'purchase') {
          // Redirect to settings with active-plans=true
          router.push('/settings?tab=upgrade_plan')
          return
        }

        let subaccountId = DEFAULT_SUBACCOUNT_ID
        let landingId = DEFAULT_LANDING_ID
        if (response.agencyUser.Permissions.length > 0 && response.landingId) {
          subaccountId = response.agencyUser.Permissions[0].subAccountId
          landingId = response.landingId
        }
        const redirectUrl = `/subaccount/${subaccountId}/landings/${landingId}/landings`
        router.push(redirectUrl)
      }
    } catch (error: any) {
      console.error('Error during login:', error.message)
      if (
        error.message
          .toLowerCase()
          .includes('invalid login credentials'.toLowerCase())
      ) {
        setError('password', { type: 'custom', message: error.message })
      } else if (
        error.message.toLowerCase().includes('user not found'.toLowerCase())
      ) {
        setError('password', { type: 'custom', message: error.message })
      } else {
        setError('password', {
          type: 'custom',
          message: 'Something went wrong.'
        })
      }
    }

    setIsLoading(false)
  }

  return {
    register,
    errors,
    handleSubmit,
    onSubmit,
    isLoading,
    getValues,
    watch
  }
}
